/**
 * useActivitiesState — les gestes de l'onglet « Activités » : la sélection multiple, l'archivage,
 * et les deux destinations du legacy.
 *
 * L'archivage est la seule écriture de l'écran : `deleteActivitiesTodo` avec l'id de l'enfant
 * (`TodoListScreen.js:176-211`). Le legacy y construit `students/undefined/activities/todo` quand
 * `student` est absent (brief § 1.5) ; ici l'id vient de `currentUser`, qui EST l'élève en mode
 * parent, donc l'URL est toujours complète. Le geste lui-même est celui des archives, aux mots
 * près : `useBulkActivityAction`.
 *
 * Après le succès, rien n'est rappelé : le réducteur retire les activités archivées de la liste
 * (`currentUser.js:1585-1607`), exactement comme sur le chemin legacy.
 */
import { useMemo } from "react"
import { useNavigation } from "@react-navigation/native"

import { activitiesService } from "../../../../../../data/services/activitiesService"
import { type UIActivity } from "../../../types"
import { useBulkActivityAction, type BulkActivityWords } from "./useBulkActivityAction"

/** Les mots du legacy, en version parent (`TodoListScreen.js:64-71`, `:181-186`). */
const ARCHIVE_WORDS: BulkActivityWords = {
    confirmTitle: (count) => `Archiver ${count} activité${count > 1 ? "s" : ""} ?`,
    confirmMessage: "Elles pourront être restaurées depuis les activités archivées.",
    doneMessage: (count) => `${count} activité${count > 1 ? "s" : ""} archivée${count > 1 ? "s" : ""}`,
    failedMessage: "Impossible d'archiver. Veuillez réessayer.",
}

export type UseActivitiesStateArgs = {
    studentId: string | number | null
    activities: UIActivity[]
}

export function useActivitiesState(args: UseActivitiesStateArgs) {
    const { studentId, activities } = args
    const navigation = useNavigation<{
        navigate: (name: string, params?: Record<string, unknown>) => void
        push: (name: string, params?: Record<string, unknown>) => void
    }>()

    const archive = useBulkActivityAction({
        studentId,
        activities,
        service: activitiesService.deleteActivitiesTodo,
        words: ARCHIVE_WORDS,
    })

    const bulk = archive.actions
    const actions = useMemo(
        () => ({
            startSelection: bulk.startSelection,
            toggleSelect: bulk.toggleSelect,
            toggleSelectAll: bulk.toggleSelectAll,
            cancelSelection: bulk.cancelSelection,
            askArchive: bulk.ask,
            cancelArchive: bulk.cancelConfirm,
            confirmArchive: bulk.run,
            dismissSnackbar: bulk.dismissSnackbar,
            /** Le menu de la page — `navigate`, comme `TodoListScreen.js:657-662`. */
            openArchived: () => navigation.navigate("archived-activites", { studentId }),
            /** « Voir plus », sous les activités faites — `TodoListScreen.js:812-829`. */
            openRealizations: () =>
                navigation.push("realizations", {
                    initialTabRealizations: true,
                    withGoBack: true,
                    ...(studentId != null ? { studentId } : {}),
                }),
        }),
        [bulk, navigation, studentId]
    )

    return {
        selection: archive.selection,
        archiveConfirm: archive.confirm,
        archiving: archive.pending,
        snackbar: archive.snackbar,
        actions,
    }
}
